import axios from 'axios';
import latinize from 'latinize';
import { createActions, handleActions } from 'redux-actions';

const actions = createActions({
    UPLOAD: {
        CREATE: ({ fieldId, existingValue }) => ({ fieldId, existingValue }),
        SIGNED_URL_REQUEST: ({ fieldId, fileName, fileSize }) => ({
            fieldId,
            fileName,
            fileSize,
        }),
        SIGNED_URL_FETCHED: ({ fieldId, assetId, signedUrl }) => ({
            fieldId,
            assetId,
            signedUrl,
        }),
        S3_UPLOAD_PROGRESS: ({ fieldId, uploadProgress }) => ({
            fieldId,
            uploadProgress,
        }),
        COMPLETE: ({ fieldId, asset }) => ({ fieldId, asset }),
        CLEAR: fieldId => ({ fieldId }),
    },
});

const initialState = {
    fields: {},
};

const fieldStatuses = [
    'CREATED',
    'SIGNING_URL',
    'SIGNED_URL',
    'UPLOADING',
    'PROCESSING',
    'COMPLETE',
];

const fieldInitialState = {
    assetId: null,
    fieldId: null,
    status: fieldStatuses[0],
    fileName: null,
    fileSize: 0,
    s3UploadUrl: null,
    previewUrl: null,
    uploadProgress: 0,
};

export default handleActions(
    {
        [actions.upload.create]: (state, action) => {
            const { fieldId, existingValue } = action.payload;

            // check if the 'existing' value is valid
            const existingValueIsValid = existingValue && existingValue.assetId;

            return Object.assign({}, state, {
                fields: {
                    ...state.fields,
                    [fieldId]: Object.assign({}, fieldInitialState, {
                        fieldId,
                        assetId: existingValueIsValid
                            ? existingValue.assetId
                            : null,
                        status: existingValueIsValid
                            ? 'COMPLETE'
                            : fieldStatuses[0],
                        fileName: existingValueIsValid
                            ? existingValue.assetFilename
                            : null,
                        fileSize: existingValueIsValid
                            ? existingValue.assetSize
                            : 0,
                        previewUrl: existingValueIsValid
                            ? existingValue.url
                            : null,
                        related: existingValueIsValid
                            ? existingValue.related
                            : null,
                        uploadProgress: existingValueIsValid ? 100 : 0,
                    }),
                },
            });
        },

        [actions.upload.signedUrlRequest]: (state, action) => {
            const { fieldId, fileName, fileSize } = action.payload;

            return Object.assign({}, state, {
                fields: {
                    ...state.fields,
                    [fieldId]: Object.assign({}, state.fields[fieldId], {
                        fileName,
                        fileSize,
                        status: 'SIGNING_URL',
                    }),
                },
            });
        },

        [actions.upload.signedUrlFetched]: {
            next: (state, action) => {
                const { fieldId, assetId, signedUrl } = action.payload;

                return Object.assign({}, state, {
                    fields: {
                        ...state.fields,
                        [fieldId]: Object.assign({}, state.fields[fieldId], {
                            assetId,
                            status: 'SIGNED_URL',
                            s3UploadUrl: signedUrl,
                        }),
                    },
                });
            },
            throw: (state, action) => {
                // const currentAssetId = action.payload.assetId;
            },
        },

        [actions.upload.s3UploadProgress]: (state, action) => {
            const { fieldId, uploadProgress } = action.payload;

            return Object.assign({}, state, {
                fields: {
                    ...state.fields,
                    [fieldId]: Object.assign({}, state.fields[fieldId], {
                        uploadProgress,
                        status:
                            uploadProgress === 100 ? 'PROCESSING' : 'UPLOADING',
                    }),
                },
            });
        },

        [actions.upload.complete]: (state, action) => {
            const { fieldId, asset } = action.payload;

            return Object.assign({}, state, {
                fields: {
                    ...state.fields,
                    [fieldId]: Object.assign({}, state.fields[fieldId], {
                        previewUrl: asset.url,
                        status: 'COMPLETE',
                    }),
                },
            });
        },

        [actions.upload.clear]: (state, action) => {
            const { fieldId } = action.payload;

            return Object.assign({}, state, {
                fields: Object.keys(
                    state.fields
                ).reduce((result, currentFieldId) => {
                    if (currentFieldId !== fieldId) {
                        result[currentFieldId] = state.fields[currentFieldId];
                    }

                    return result;
                }, {}),
            });
        },
    },
    initialState
);

export const createUploadField = (fieldId, existingValue) => {
    return actions.upload.create({ fieldId, existingValue });
};

export const startUpload = (fieldId, file, assetType) => {
    const normalizedFileName = file.name
        .toLowerCase()
        .replace(/([^a-zA-Z0-9\-_.]+)/gi, '-');

    const fileName = latinize(normalizedFileName);
    const fileSize = file.size;
    const contentType = file.type;

    return (dispatch, getState, api) => {
        dispatch(
            actions.upload.signedUrlRequest({ fieldId, fileName, fileSize })
        );

        let newAssetId = null;

        return api
            .post('/assets/s3/sign', {
                objectName: fileName,
                contentType,
                assetType,
            })
            .then(
                response => {
                    const { assetId, signedUrl } = response.data;

                    // store reference to our asset ID for the prmosie chain
                    newAssetId = assetId;

                    dispatch(
                        actions.upload.signedUrlFetched({
                            fieldId,
                            assetId,
                            signedUrl,
                        })
                    );

                    return {
                        assetId,
                        signedUrl,
                    };
                },
                err => dispatch(actions.download.fetched(err))
            )
            .then(({ assetId, signedUrl }) => {
                dispatch(
                    actions.upload.s3UploadProgress({
                        fieldId,
                        uploadProgress: 0,
                    })
                );

                // start upload to s3
                return axios.put(signedUrl, file, {
                    headers: {
                        'Content-Type': contentType,
                    },
                    onUploadProgress: e => {
                        if (e.lengthComputable) {
                            const uploadProgress = Math.round(
                                e.loaded / e.total * 100
                            );

                            dispatch(
                                actions.upload.s3UploadProgress({
                                    fieldId,
                                    uploadProgress,
                                })
                            );
                        }
                    },
                });
            })
            .then(() => {
                // finished uploading to s3, now update the asset on
                // our server to indicate upload has finished
                return api.post(`/assets/${newAssetId}/markUploadComplete`, {});
            })
            .then(response => {
                dispatch(
                    actions.upload.complete({ fieldId, asset: response.data })
                );

                return response.data;
            });
    };
};

export const clearUploadField = fieldId => {
    return actions.upload.clear(fieldId);
};



// WEBPACK FOOTER //
// ./src/redux/reducers/asset.js